/**
 * ReportsPage — casca dos relatórios: cabeçalho, exportação (por aba), filtro de
 * período e o alternador de abas.
 *
 * Os relatórios em si vivem em `components/reports/` para esta página não virar
 * um arquivo de 1.500 linhas. As abas usam o `<Tabs>` base do app — a cor azul já
 * vem do componente, nada de estilizar aqui (CLAUDE.md #25).
 *
 * ─── O DESENHO OFICIAL NUMA TELA DE ABAS SEM VALOR AO VIVO ──────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes` e `/financeiro`. Duas traduções:
 *
 * · O EYEBROW diz a FAMÍLIA ("Relatórios") e o H1 acompanha a ABA — o arranjo
 *   que `/financeiro` estreou (#200). Um h1 fixo com os números do Estoque
 *   embaixo seria a mentira do #182 uma linha acima do número.
 *
 * · O VALOR AO VIVO NÃO É UM NÚMERO, e isso é decisão, não omissão. Cada aba já
 *   abre com os próprios KPIs a dois centímetros do hero: repetir um deles aqui
 *   é exatamente a duplicação que o #200 removeu do Financeiro, com o agravante
 *   de que aqui ela teria de ser recalculada fora do componente que a mostra —
 *   duas contas do mesmo número, prontas para discordar na primeira correção de
 *   uma delas. Pior: TODA aba tem portão próprio (plano, papel, `inventory`) e
 *   estado de falha, então metade dos estados exibiria um número sobre dado que
 *   o usuário não pode ver (o `[]` da recusa virando "R$ 0,00" do #187).
 *   O que o hero afirma é o PERÍODO — o recorte que qualifica todo número da
 *   tela, que muda ao vivo com o filtro e que nenhum cartão de aba enuncia.
 *
 * ─── A LEITURA DE `quotes` (o bug que este grupo corrige) ────────────────────
 *
 * A página lia `quotes` da empresa no período **sem filtrar `document_type`**, e
 * a Venda Rápida grava a venda como `quotes` com `document_type='pedido'` — logo
 * as vendas de balcão entravam no relatório de orçamentos e inflavam tudo. Agora
 * a leitura traz as duas coisas e `partitionQuotesForReport` separa: orçamentos
 * alimentam os KPIs, PDV vira card próprio (a receita não pode simplesmente
 * sumir da tela). A partição vale também para o **cache offline**, onde não há
 * SQL para filtrar.
 *
 * A leitura agora também **PAGINA** (armadilha #50): sem `.range()` o PostgREST
 * corta em 1.000 linhas sem avisar e o relatório sai menor que a realidade.
 */
import { useState, useEffect, useMemo } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart3,
  ArrowLeft,
  Crown,
  Loader2,
  Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { useXlsxExport } from '@/hooks/useXlsxExport';
import {
  buildDocumentsXlsx,
  buildFinanceXlsx,
  buildItemsXlsx,
  buildQuotesXlsx,
  buildStockXlsx,
} from '@/lib/xlsxTemplates';
import { DateRangeFilter, DateRange } from '@/components/reports/DateRangeFilter';
import { companyWeekStartsOn, PERIOD_PRESET_LABELS } from '@/lib/reportPeriod';
import { QuotesReport, type QuoteData } from '@/components/reports/QuotesReport';
import { PaymentsReport } from '@/components/reports/PaymentsReport';
import { StockReport } from '@/components/reports/StockReport';
import { ItemsReport } from '@/components/reports/ItemsReport';
import { DocumentsReport } from '@/components/reports/DocumentsReport';
import { ReportFiltersSheet } from '@/components/reports/ReportFiltersSheet';
import { FilteredEmptyState, WarningCard } from '@/components/reports/ReportGates';
import type { ReportViewMode } from '@/components/reports/ReportViewToggle';
import { useStock, useStockMovementsInRange } from '@/hooks/useStock';
import { useReportItems } from '@/hooks/useReportItems';
import { useReportDocuments } from '@/hooks/useReportDocuments';
import { useReportDimensions } from '@/hooks/useReportDimensions';
import { useReportPayments } from '@/hooks/useReportPayments';
import { useFinanceCategoriesReadOnly } from '@/hooks/useProfitSettings';
import {
  alertKindOf,
  balanceOf,
  DEFAULT_STOCK_REPORT_SORT,
  STOCK_ALERT_LABELS,
  STOCK_REPORT_SORT_FIELDS,
  type StockReportSort,
} from '@/lib/stockReport';
import { productUnitCost } from '@/lib/cost';
import { exportToCSV, formatReportData } from '@/lib/csvExport';
import { fetchAllPages } from '@/lib/reportPaging';
import { partitionQuotesForReport } from '@/lib/reportScope';
import {
  ADHOC_ITEM_BADGE,
  computeItemsReport,
  DEFAULT_ITEMS_REPORT_SORT,
  ITEMS_REPORT_SORT_FIELDS,
  ITEM_KIND_LABELS,
  sortItemsReport,
  type ItemsReportSort,
} from '@/lib/itemsReport';
import {
  computeContractsReport,
  computeReceiptsReport,
  computeServiceOrdersReport,
  DEFAULT_DOCUMENTS_REPORT_SORT,
  DOCUMENTS_REPORT_SORT_FIELDS,
  type DocumentsReportSort,
} from '@/lib/documentsReport';
import { resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import { toast } from 'sonner';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { getCachedQuotes } from '@/lib/offlineDataCache';
import { getCachedFinanceCategories } from '@/lib/offlineDataCache';
import { PAYMENT_METHOD_LABELS, STATUS_LABELS } from '@/lib/financeLabels';
import {
  effectiveStatus,
  DEFAULT_PAYMENTS_REPORT_SORT,
  PAYMENTS_REPORT_SORT_FIELDS,
  scopeEntries,
  toIsoDateRange,
  UNSPECIFIED_METHOD_LABEL,
  type PaymentDateBasis,
  type PaymentsReportSort,
} from '@/lib/paymentsReport';
import {
  activeFilterCount,
  clearFiltersForTab,
  EMPTY_REPORT_FILTERS,
  quoteMatchesReportFilters,
  type ReportFilterState,
  type ReportTab,
} from '@/lib/reportFilters';
import {
  DEFAULT_QUOTES_REPORT_SORT,
  QUOTES_REPORT_SORT_FIELDS,
  quoteSortServerColumn,
  sortQuoteRows,
  type QuotesReportSort,
} from '@/lib/quotesReport';

/** Preset inicial — o mesmo de sempre (últimos 6 meses). */
const DEFAULT_PRESET: ReportPeriodPreset = '6m';

/** O h1 do hero acompanha a aba (ver o cabeçalho). */
const TAB_TITLES: Record<ReportTab, string> = {
  quotes: 'Orçamentos',
  items: 'Itens',
  payments: 'Pagamentos',
  stock: 'Estoque',
  documents: 'Documentos',
};

type ReportSortValue = { field: string; direction: 'asc' | 'desc' };

const DEFAULT_REPORT_SORTS: Record<ReportTab, ReportSortValue> = {
  quotes: DEFAULT_QUOTES_REPORT_SORT,
  items: DEFAULT_ITEMS_REPORT_SORT,
  payments: DEFAULT_PAYMENTS_REPORT_SORT,
  stock: DEFAULT_STOCK_REPORT_SORT,
  documents: DEFAULT_DOCUMENTS_REPORT_SORT,
};

function newFilterState(): ReportFilterState {
  return {
    ...EMPTY_REPORT_FILTERS,
    statusByTab: { ...EMPTY_REPORT_FILTERS.statusByTab },
  };
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const reportsAdvanced = useFeature('reports_advanced');
  const reportsExport = useFeature('reports_export');
  // O relatório de pagamentos continua PRO por `reports_advanced`, além do
  // gate de papel (a RLS de finance_entries é owner/admin).
  // O relatório de estoque tem gate só de PLANO: a RLS de `stock_movements` é
  // SELECT para qualquer MEMBRO (≠ finance_entries, owner/admin-only), então
  // aqui NÃO entra `canSeeFinance`. Itens e Documentos seguem a mesma regra: as
  // RLS de `quote_items`, `service_orders`, `contracts` e `receipts` são todas
  // `is_company_member` (conferido no pg_policies), então só há gate de plano.
  const inventory = useFeature('inventory');
  const { canSeeFinance } = useRole();
  const { exportXlsx, allowed: xlsAllowed } = useXlsxExport();
  const { isOnline } = useNetworkStatus();

  const [tab, setTab] = useState<ReportTab>('quotes');
  const [loading, setLoading] = useState(true);
  /** Todas as linhas de `quotes` do período — orçamentos E vendas do PDV. */
  const [quotes, setQuotes] = useState<QuoteData[]>([]);
  const [quotesTruncated, setQuotesTruncated] = useState(false);
  /** Base de data do relatório de pagamentos — escolha explícita do usuário. */
  const [basis, setBasis] = useState<PaymentDateBasis>('paid');
  /**
   * Resumo × Detalhado. Vale para todas as abas de propósito: quem quer a lista
   * completa quer em qualquer recorte, e um controle por aba faria o usuário
   * reescolher a cada troca.
   */
  const [viewMode, setViewMode] = useState<ReportViewMode>('summary');
  /**
   * Cliente/Produto persistem ao trocar para outra aba compatível. Em aba onde
   * não se aplicam, somem e não contam no badge; o valor é preservado para a
   * volta. Status é independente por aba porque os vocabulários não se misturam.
   */
  const [filters, setFilters] = useState<ReportFilterState>(newFilterState);
  /** Cada aba preserva sua própria classificação e seu padrão histórico. */
  const [sortByTab, setSortByTab] = useState<Record<ReportTab, ReportSortValue>>(
    () => ({ ...DEFAULT_REPORT_SORTS }),
  );

  // Preset e faixa andam juntos: o preset é o que dá o RÓTULO honesto do total
  // (deduzir pela quantidade de dias não distingue "Hoje" de "Ontem" e já fez a
  // tela escrever o período errado — armadilha #45).
  const [preset, setPreset] = useState<ReportPeriodPreset>(DEFAULT_PRESET);
  const [dateRange, setDateRange] = useState<DateRange>(
    () => resolvePeriodPreset(DEFAULT_PRESET) ?? { from: new Date(), to: new Date() },
  );

  const dimensions = useReportDimensions();
  const selectedProduct = dimensions.products.find((product) => product.id === filters.productId);
  const clientNames = useMemo(
    () => new Map(dimensions.clients.map((client) => [client.id, client.name])),
    [dimensions.clients],
  );
  const currentSort = sortByTab[tab];
  const currentFilterCount = activeFilterCount(filters, tab);

  // Serviço e produto sem rastreio são dimensões válidas em Itens/Orçamentos,
  // mas não existem na posição de Estoque. Ao chegar nessa aba, um valor assim
  // deixa de ser preservável: limpá-lo evita um filtro impossível e invisível.
  useEffect(() => {
    if (
      tab === 'stock'
      && selectedProduct
      && (selectedProduct.type !== 'product' || !selectedProduct.trackStock)
    ) {
      setFilters((current) => ({ ...current, productId: null }));
    }
  }, [tab, selectedProduct]);

  const sortOptions = tab === 'quotes'
    ? QUOTES_REPORT_SORT_FIELDS
    : tab === 'items'
      ? ITEMS_REPORT_SORT_FIELDS
      : tab === 'payments'
        ? PAYMENTS_REPORT_SORT_FIELDS
        : tab === 'stock'
          ? STOCK_REPORT_SORT_FIELDS
          : DOCUMENTS_REPORT_SORT_FIELDS;

  const updateCurrentSort = (patch: Partial<ReportSortValue>) => {
    setSortByTab((current) => ({
      ...current,
      [tab]: { ...current[tab], ...patch },
    }));
  };

  const updateReportFilters = (next: ReportFilterState) => {
    const previousStatus = filters.statusByTab.payments;
    const nextStatus = next.statusByTab.payments;
    if (nextStatus !== previousStatus && nextStatus) {
      // Quitado é ancorado no pagamento; estados sem pagamento usam vencimento.
      // A troca fica visível no seletor da própria aba, sem base implícita.
      setBasis(nextStatus === 'paid' ? 'paid' : 'due');
    }
    setFilters(next);
  };

  // Load cached data immediately on mount (critical for offline app restart)
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const cached = getCachedQuotes(user.id);
    if (cached && cached.length > 0) {
      const filteredCached = cached
        .filter((q: QuoteData) => {
          if ((q as any).company_id && (q as any).company_id !== activeCompany?.id) return false;
          const createdAt = new Date(q.created_at);
          return createdAt >= dateRange.from && createdAt <= dateRange.to;
        })
        .filter((q: QuoteData) => quoteMatchesReportFilters(q, filters, 'quotes'))
        .map((q: any) => ({
          ...q,
          quote_items: q.quote_items ?? q._offline_items ?? q.items ?? [],
        }));
      setQuotes(sortQuoteRows(filteredCached as QuoteData[], sortByTab.quotes as QuotesReportSort));
      setQuotesTruncated(false);
    }
    setLoading(false);
  }, [
    user,
    activeCompany?.id,
    dateRange,
    filters.clientId,
    filters.productId,
    filters.statusByTab.quotes,
    sortByTab.quotes,
  ]);

  // Fetch from server when online or date range changes (re-runs on company switch)
  useEffect(() => {
    if (!user || !isOnline || !activeCompany) return;

    const fetchQuotes = async () => {
      try {
        // `document_type` vem junto: a partição (orçamento × PDV) é feita no
        // cliente, para a receita do balcão continuar visível em card próprio em
        // vez de sumir do relatório.
        const productId = filters.productId;
        const itemRelation = productId ? 'quote_items!inner' : 'quote_items';
        const quoteSort = sortByTab.quotes as QuotesReportSort;
        const { rows, truncated } = await fetchAllPages<QuoteData>((start, end) => {
          let query: any = supabase
            .from('quotes')
            .select(
              'id, status, total, created_at, client_id, client_name, title, document_type,'
              + ` ${itemRelation}(product_id)`,
            )
            .eq('company_id', activeCompany.id)
            .gte('created_at', dateRange.from.toISOString())
            .lte('created_at', dateRange.to.toISOString());

          // BLOCO ORÇAMENTOS: dimensões no servidor antes da página.
          if (filters.clientId) query = query.eq('client_id', filters.clientId);
          if (filters.statusByTab.quotes) {
            query = query.eq('status', filters.statusByTab.quotes);
          }
          if (productId) query = query.eq('quote_items.product_id', productId);

          return query
            .order(quoteSortServerColumn(quoteSort.field), {
              ascending: quoteSort.direction === 'asc',
              nullsFirst: false,
            })
            .order('id', { ascending: true })
            .range(start, end);
        });

        setQuotes(
          sortQuoteRows(
            rows.filter((row) => quoteMatchesReportFilters(row, filters, 'quotes')),
            quoteSort,
          ),
        );
        setQuotesTruncated(truncated);
      } catch (err) {
        console.error('Error fetching quotes:', err);
      }
    };

    fetchQuotes();
  }, [
    user,
    dateRange,
    isOnline,
    activeCompany?.id,
    filters.clientId,
    filters.productId,
    filters.statusByTab.quotes,
    sortByTab.quotes,
  ]);

  /**
   * Orçamentos × vendas do PDV. A partição vale para os dois caminhos (servidor
   * e cache offline) — era justamente o cache que continuaria contaminado se o
   * filtro vivesse só no SQL.
   */
  const scope = useMemo(() => {
    const inRange = quotes.filter((q) => {
      const createdAt = new Date(q.created_at);
      return createdAt >= dateRange.from && createdAt <= dateRange.to;
    });
    return partitionQuotesForReport(inRange);
  }, [quotes, dateRange]);

  const filteredQuotes = scope.quotes;

  // Intervalo em 'YYYY-MM-DD' do calendário LOCAL (as colunas de data do
  // financeiro são DATE, sem fuso — converter por toISOString derrubaria o dia
  // em UTC-3).
  const isoRange = useMemo(
    () => toIsoDateRange(dateRange.from, dateRange.to),
    [dateRange],
  );

  // Lançamentos do financeiro para a exportação da aba Pagamentos. A queryKey do
  // hook inclui o company_id, então trocar de empresa recarrega.

  /**
   * Categorias para rotular a aba "Categorias" do XLSX. Leitura AVULSA de
   * propósito, em vez de `useFinanceCategories`: aquele hook SEMEIA as 10
   * categorias padrão quando a empresa ainda não tem nenhuma, e abrir
   * /relatorios não pode criar linhas no financeiro de quem nunca entrou lá.
   * Cai no cache offline quando a leitura falha.
   */
  const loadFinanceCategories = async () => {
    const cached = user?.id ? getCachedFinanceCategories(user.id) ?? [] : [];
    if (!activeCompany?.id || !isOnline) return cached;
    const { data, error } = await supabase
      .from('finance_categories')
      .select('id, name, kind')
      .eq('company_id', activeCompany.id);
    if (error) return cached;
    return data ?? cached;
  };

  const paymentsAllowed = reportsAdvanced.allowed && canSeeFinance;
  const {
    entries: financeEntries,
    truncated: paymentsTruncated,
    isLoading: paymentsLoading,
    isError: paymentsFailed,
  } = useReportPayments({
    range: isoRange,
    basis,
    filters,
    sort: sortByTab.payments as PaymentsReportSort,
    enabled: tab === 'payments' && paymentsAllowed,
  });
  const { categories: reportFinanceCategories } = useFinanceCategoriesReadOnly(
    tab === 'payments' && paymentsAllowed,
  );
  const financeCategoryNames = useMemo(
    () => new Map(
      reportFinanceCategories.map((category: any) => [
        String(category.id),
        String(category.name),
      ]),
    ),
    [reportFinanceCategories],
  );

  // ===== itens vendidos =====

  const {
    documents: itemDocuments,
    truncated: itemsTruncated,
    isLoading: itemsLoading,
    isError: itemsFailed,
  } = useReportItems({
    range: dateRange,
    filters,
    enabled: tab === 'items' && reportsAdvanced.allowed,
  });

  const itemsReport = useMemo(
    () => sortItemsReport(
      computeItemsReport(itemDocuments, isoRange),
      sortByTab.items as ItemsReportSort,
    ),
    [itemDocuments, isoRange, sortByTab.items],
  );

  // ===== documentos (OS, contratos, recibos) =====

  const {
    serviceOrders,
    contracts: contractDocs,
    receipts: receiptDocs,
    truncated: documentsTruncated,
    isLoading: documentsLoading,
    isError: documentsFailed,
  } = useReportDocuments({
    range: dateRange,
    isoRange,
    filters,
    sort: sortByTab.documents as DocumentsReportSort,
    enabled: tab === 'documents' && reportsAdvanced.allowed,
  });

  const ordersReport = useMemo(
    () => computeServiceOrdersReport(
      serviceOrders,
      isoRange,
      sortByTab.documents as DocumentsReportSort,
    ),
    [serviceOrders, isoRange, sortByTab.documents],
  );
  const contractsReport = useMemo(
    () => computeContractsReport(
      contractDocs,
      isoRange,
      sortByTab.documents as DocumentsReportSort,
    ),
    [contractDocs, isoRange, sortByTab.documents],
  );
  const receiptsReport = useMemo(
    () => computeReceiptsReport(
      receiptDocs,
      isoRange,
      sortByTab.documents as DocumentsReportSort,
    ),
    [receiptDocs, isoRange, sortByTab.documents],
  );

  // ===== estoque =====

  /**
   * O período do estoque é de DIAS INTEIROS: `created_at` de `stock_movements` é
   * um timestamp, e os presets do filtro carregam a hora atual (subDays(now, 7)
   * às 14h excluiria um movimento das 9h daquele mesmo dia). Um relatório que
   * ignora meio dia da ponta seria errado sem parecer errado.
   */
  const stockRange = useMemo(
    () => ({ from: startOfDay(dateRange.from), to: endOfDay(dateRange.to) }),
    [dateRange],
  );

  // Unidades ESTOCÁVEIS (a variação, nunca o pai) — o hook já resolve isso.
  // A queryKey inclui o company_id, então trocar de empresa recarrega.
  const {
    trackedProducts,
    productById,
    productsTruncated,
    isLoading: stockProductsLoading,
    isError: stockProductsFailed,
  } = useStock({
    filters,
    sort: sortByTab.stock as StockReportSort,
    selectedParentId: selectedProduct?.parentProductId ?? null,
  });
  const {
    movements: stockMovements,
    truncated: stockTruncated,
    isLoading: stockLoading,
    isError: stockMovementsFailed,
  } = useStockMovementsInRange(stockRange, {
    // Só lê a razão quando a aba está aberta e o plano permite — abrir
    // /relatorios no Orçamentos não deve puxar meses de movimentos.
    enabled: tab === 'stock' && inventory.allowed,
    filters,
  });

  /**
   * Nome do PRODUTO da unidade, para as exportações (que têm coluna "Variação"
   * à parte). A variação já herda o nome do pai em `products.name`; resolver
   * pelo `productById` cobre o caso de o pai ter sido renomeado sem a filha ter
   * sido reescrita.
   */
  const stockUnitName = (unit: any): string => {
    const parent = unit?.parent_product_id
      ? productById.get(String(unit.parent_product_id))?.name
      : null;
    return String(parent ?? unit?.name ?? '').trim() || 'Produto';
  };

  // ===== exportação (por aba ativa) =====

  const paymentExportBasis: PaymentDateBasis =
    filters.statusByTab.payments
    && filters.statusByTab.payments !== 'paid'
      ? 'due'
      : basis;

  const handleExportCSV = async () => {
    if (!reportsExport.require()) return;

    try {
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      if (tab === 'payments') {
        const rows = scopeEntries(financeEntries, isoRange, paymentExportBasis).map((e) => ({
          'Tipo': e.kind === 'income' ? 'Entrada' : 'Saída',
          'Descrição': e.description,
          'Valor': Number(e.amount ?? 0).toFixed(2).replace('.', ','),
          'Vencimento': e.due_date ? e.due_date.split('-').reverse().join('/') : '',
          'Pagamento': e.paid_date ? e.paid_date.split('-').reverse().join('/') : '',
          'Status': STATUS_LABELS[effectiveStatus(e)] ?? effectiveStatus(e),
          // payment_method é nullable — o balde explícito evita confundir com
          // "Outro", que é um método real.
          'Forma de pagamento': e.payment_method
            ? PAYMENT_METHOD_LABELS[e.payment_method]
            : UNSPECIFIED_METHOD_LABEL,
        }));
        await exportToCSV(rows, `relatorio-pagamentos-${dateStr}.csv`);
      } else if (tab === 'items') {
        const rows = itemsReport.items.map((item) => ({
          'Item': item.name,
          'Origem': item.productId ? 'Catálogo' : ADHOC_ITEM_BADGE,
          'Tipo': ITEM_KIND_LABELS[item.kind],
          'Unidade': item.unit || '',
          'Quantidade': String(item.quantity).replace('.', ','),
          'Linhas': String(item.lines),
          'Documentos': String(item.documents),
          'Receita': item.revenue.toFixed(2).replace('.', ','),
          // Sem custo informado a célula fica VAZIA — 0 diria "custou nada".
          'Custo': item.margin == null ? '' : item.cost.toFixed(2).replace('.', ','),
          'Margem': item.margin == null ? '' : item.margin.toFixed(2).replace('.', ','),
          'Margem %':
            item.marginPercent == null ? '' : item.marginPercent.toFixed(1).replace('.', ','),
          'Linhas sem custo': String(item.missingCostLines),
        }));
        await exportToCSV(rows, `relatorio-itens-${dateStr}.csv`);
      } else if (tab === 'documents') {
        // O CSV é uma folha só, com os três tipos. Assim um status contextual
        // de contrato/recibo nunca exporta um arquivo vazio com linhas na tela.
        const rows = [
          ...ordersReport.rows.map((row) => ({
            'Tipo': 'Ordem de serviço',
            'Data': row.date ? row.date.split('-').reverse().join('/') : '',
            'Título': row.title,
            'Cliente': row.clientName,
            'Status': row.statusLabel,
            'Total': row.total.toFixed(2).replace('.', ','),
          })),
          ...contractsReport.rows.map((row) => ({
            'Tipo': 'Contrato',
            'Data': row.date ? row.date.split('-').reverse().join('/') : '',
            'Título': row.title,
            'Cliente': row.clientName,
            'Status': row.statusLabel,
            'Total': row.total.toFixed(2).replace('.', ','),
          })),
          ...receiptsReport.rows.map((row) => ({
            'Tipo': 'Recibo',
            'Data': row.date ? row.date.split('-').reverse().join('/') : '',
            'Título': `Recibo ${row.id}`,
            'Cliente': row.payerName,
            'Status': row.stateLabel,
            'Total': row.amount.toFixed(2).replace('.', ','),
          })),
        ];
        await exportToCSV(rows, `relatorio-documentos-${dateStr}.csv`);
      } else if (tab === 'stock') {
        // O CSV é uma folha só → exporta a POSIÇÃO (a lista acionável). A razão
        // do período sai na aba "Movimentações" do XLSX.
        const rows = trackedProducts.map((u: any) => {
          const balance = balanceOf(u);
          const kind = alertKindOf(u);
          const price = Number(u.price ?? 0) || 0;
          const cost = productUnitCost(u);
          return {
            'Produto': stockUnitName(u),
            'Variação': u.variant_name || '',
            'Categoria': u.category || '',
            'Unidade': u.unit || '',
            'Saldo': String(balance).replace('.', ','),
            // Mínimo NULO é "sem alerta", não zero — fica vazio.
            'Mínimo': u.min_stock == null ? '' : String(u.min_stock).replace('.', ','),
            'Situação': kind ? STOCK_ALERT_LABELS[kind] : 'OK',
            'Preço de venda': price.toFixed(2).replace('.', ','),
            // Rótulo honesto: é receita potencial, não o valor investido.
            'Potencial de venda': (balance > 0 ? balance * price : 0)
              .toFixed(2)
              .replace('.', ','),
            // Colunas novas no FIM (não desloca as antigas). Sem custo informado
            // as duas ficam VAZIAS — zero seria dizer "custou nada".
            'Custo': cost == null ? '' : cost.toFixed(2).replace('.', ','),
            'Valor investido':
              cost == null
                ? ''
                : (balance > 0 ? balance * cost : 0).toFixed(2).replace('.', ','),
          };
        });
        await exportToCSV(rows, `relatorio-estoque-${dateStr}.csv`);
      } else {
        // Orçamentos: as vendas do PDV NÃO entram, exatamente como nos KPIs —
        // uma exportação que contradiz a tela é pior que nenhuma.
        const data = formatReportData(filteredQuotes);
        await exportToCSV(data, `relatorio-orcamentos-${dateStr}.csv`);
      }
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  // Handle XLSX export (PRO). exceljs is loaded lazily via dynamic import.
  const handleExportXLSX = () => {
    if (tab === 'payments') {
      exportXlsx(
        async () =>
          buildFinanceXlsx(
            financeEntries,
            await loadFinanceCategories(),
            isoRange.from,
            isoRange.to,
            { basis: paymentExportBasis, includePaymentMethods: true },
          ),
        'pagamentos',
        { successMessage: 'Relatório exportado com sucesso!' },
      );
      return;
    }
    if (tab === 'items') {
      exportXlsx(() => buildItemsXlsx(itemsReport), 'itens', {
        successMessage: 'Relatório exportado com sucesso!',
      });
      return;
    }
    if (tab === 'documents') {
      exportXlsx(
        () => buildDocumentsXlsx(ordersReport, contractsReport, receiptsReport),
        'documentos',
        { successMessage: 'Relatório exportado com sucesso!' },
      );
      return;
    }
    if (tab === 'stock') {
      // "Produto" leva o nome do pai e "Variação" o rótulo — as três variações
      // ficam distinguíveis sem repetir "Camiseta › " em toda linha.
      const units = trackedProducts.map((u: any) => ({ ...u, name: stockUnitName(u) }));
      exportXlsx(() => buildStockXlsx(units, stockMovements), 'estoque', {
        successMessage: 'Relatório exportado com sucesso!',
      });
      return;
    }
    exportXlsx(() => buildQuotesXlsx(filteredQuotes), 'orcamentos', {
      successMessage: 'Relatório exportado com sucesso!',
    });
  };

  const scopedPaymentEntries = useMemo(
    () => (paymentsAllowed ? scopeEntries(financeEntries, isoRange, paymentExportBasis) : []),
    [paymentsAllowed, financeEntries, isoRange, paymentExportBasis],
  );

  // O botão de exportar reflete a aba ativa: sem dado na aba, nada a exportar.
  // No estoque a POSIÇÃO já é exportável mesmo sem movimento no período (é a
  // fotografia de agora, não um recorte do período).
  const hasData =
    tab === 'payments'
      ? scopedPaymentEntries.length > 0
      : tab === 'items'
        ? itemsReport.items.length > 0
        : tab === 'documents'
          ? ordersReport.count > 0
            || contractsReport.count > 0
            || receiptsReport.count > 0
          : tab === 'stock'
            ? trackedProducts.length > 0 || stockMovements.length > 0
            : filteredQuotes.length > 0;

  const currentTabLoading =
    tab === 'items'
      ? itemsLoading
      : tab === 'payments'
        ? paymentsLoading
        : tab === 'stock'
          ? stockLoading || stockProductsLoading
          : tab === 'documents'
            ? documentsLoading
            : false;
  const currentTabFailed =
    tab === 'items'
      ? itemsFailed
      : tab === 'payments'
        ? paymentsFailed
        : tab === 'stock'
          ? stockProductsFailed || stockMovementsFailed
        : tab === 'documents'
          ? documentsFailed
          : false;
  const currentTabHasNoData =
    tab === 'quotes'
      ? filteredQuotes.length === 0 && scope.pdvSummary.count === 0
      : tab === 'items'
        ? itemsReport.items.length === 0
        : tab === 'payments'
          ? financeEntries.length === 0
          : tab === 'stock'
            ? trackedProducts.length === 0 && stockMovements.length === 0
            : ordersReport.count === 0
              && contractsReport.count === 0
              && receiptsReport.count === 0;
  const currentGateAllows =
    tab === 'payments'
      ? paymentsAllowed
      : tab === 'stock'
        ? inventory.allowed
        : reportsAdvanced.allowed;
  const showFilteredEmpty =
    currentFilterCount > 0
    && currentGateAllows
    && !currentTabLoading
    && !currentTabFailed
    && currentTabHasNoData;
  const currentTruncated =
    tab === 'quotes'
      ? quotesTruncated
      : tab === 'items'
        ? itemsTruncated
        : tab === 'payments'
          ? paymentsTruncated
          : tab === 'stock'
            ? stockTruncated || productsTruncated
            : documentsTruncated;
  const clearCurrentFilters = () => setFilters((current) => clearFiltersForTab(current, tab));

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BarChart3 className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Relatórios</h2>
          <p className="mb-4 text-muted-foreground">Faça login para ver seus relatórios</p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes)                */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/reports` não está na navegação inferior: sem este botão, a
                única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate(-1)}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Relatórios</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">{TAB_TITLES[tab]}</h1>
            </div>
            {/* A exportação é a única ação da tela e vira ÍCONE (#186), com o
                nome no `aria-label`/`title` — sem eles ela perderia o nome
                acessível junto com o texto (#142). O menu (CSV × XLSX, cada um
                com o próprio selo de PRO) continua idêntico. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={!hasData}
                  className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white disabled:opacity-40"
                  aria-label="Exportar"
                  title="Exportar"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleExportCSV}
                  disabled={!isOnline}
                  title={!isOnline ? 'Exportar CSV requer conexão com a internet' : ''}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  CSV
                  {!reportsExport.allowed && (
                    <Crown className="h-3.5 w-3.5 ml-auto text-amber-500" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportXLSX}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  XLSX (Excel)
                  {!xlsAllowed && (
                    <Crown className="h-3.5 w-3.5 ml-auto text-amber-500" />
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* O "valor ao vivo" desta tela é o PERÍODO (ver o cabeçalho): ele é o
              recorte que qualifica TODO número das cinco abas, muda ao vivo com
              o filtro e não é enunciado por nenhum cartão. O rótulo vem do
              PRESET, nunca de uma dedução por quantidade de dias — deduzir já
              fez a tela escrever "12 meses" com 6 selecionados (#45/#83). */}
          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">Período</p>
            <p className="truncate text-2xl font-bold leading-tight">
              {PERIOD_PRESET_LABELS[preset]}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {format(dateRange.from, 'dd/MM/yyyy')} – {format(dateRange.to, 'dd/MM/yyyy')}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
        <Tabs value={tab} onValueChange={(v) => setTab(v as ReportTab)}>
          {/* 5 abas não cabem numa linha em 360px: o grid quebra em duas
              (3 + 2) e o `h-auto` deixa a pill crescer com elas. */}
          {/* Faixa ROLÁVEL de uma linha. Numa grade de 3 colunas as 5 abas caíam em
              DUAS linhas, deixando a pílula com formato irregular e rebaixando
              visualmente "Estoque" e "Documentos", que não são menos importantes
              — só chegaram depois. */}
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="quotes" className="h-9 shrink-0 px-3 text-xs">Orçamentos</TabsTrigger>
            <TabsTrigger value="items" className="h-9 shrink-0 px-3 text-xs">Itens</TabsTrigger>
            <TabsTrigger value="payments" className="h-9 shrink-0 px-3 text-xs">Pagamentos</TabsTrigger>
            <TabsTrigger value="stock" className="h-9 shrink-0 px-3 text-xs">Estoque</TabsTrigger>
            <TabsTrigger value="documents" className="h-9 shrink-0 px-3 text-xs">Documentos</TabsTrigger>
          </TabsList>

          {/* CONTROLES DO RECORTE — período, dimensões e ordenação.
              Ficam DENTRO do `<Tabs>` (mas fora do `<TabsContent>`) porque
              valem para as cinco abas, e logo abaixo da faixa de abas porque
              formam com ela um grupo só: todos respondem "o que eu estou
              vendo". O `space-y-2` é o do grupo — o `space-y-5` de fora separa
              SEÇÕES, e usá-lo aqui empurraria o primeiro cartão para baixo da
              dobra (#191). */}
          <div className="mt-4 space-y-2">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                preset={preset}
                onPresetChange={setPreset}
                weekStartsOn={companyWeekStartsOn(activeCompany)}
              />
              <ReportFiltersSheet
                tab={tab}
                filters={filters}
                onFiltersChange={updateReportFilters}
                clients={dimensions.clients}
                products={dimensions.products}
                sortOptions={sortOptions}
                sortField={currentSort.field}
                sortDirection={currentSort.direction}
                onSortFieldChange={(field) => updateCurrentSort({ field })}
                onSortDirectionChange={(direction) => updateCurrentSort({ direction })}
              />
            </div>

            {dimensions.truncated && (
              <WarningCard>
                A lista de opções de Cliente/Produto atingiu o limite de segurança. Refine o
                cadastro antes de escolher um filtro.
              </WarningCard>
            )}
            {tab === 'items' && currentTruncated && (
              <WarningCard>
                A classificação de Itens considera apenas as linhas carregadas. Reduza o
                período ou aplique filtros para obter a ordem completa.
              </WarningCard>
            )}
            {tab === 'stock' && productsTruncated && (
              <WarningCard>
                A posição de estoque atingiu o limite de segurança. A classificação
                considera apenas os produtos carregados; aplique um Produto para obter
                a lista completa.
              </WarningCard>
            )}
          </div>

          <TabsContent value="quotes" className="mt-4">
            {showFilteredEmpty ? (
              <FilteredEmptyState activeCount={currentFilterCount} onClear={clearCurrentFilters} />
            ) : (
              <QuotesReport
                filteredQuotes={filteredQuotes}
                pdvSales={scope.pdvSummary}
                dateRange={dateRange}
                preset={preset}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                truncated={quotesTruncated}
                onOpenItems={() => setTab('items')}
                allowed={reportsAdvanced.allowed}
                onUnlock={() => reportsAdvanced.require()}
              />
            )}
          </TabsContent>

          <TabsContent value="items" className="mt-4">
            {showFilteredEmpty ? (
              <FilteredEmptyState activeCount={currentFilterCount} onClear={clearCurrentFilters} />
            ) : (
              <ItemsReport
                report={itemsReport}
                pdvExcludedCount={scope.pdvSummary.count}
                truncated={itemsTruncated}
                loading={itemsLoading}
                loadFailed={itemsFailed}
                isOnline={isOnline}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                allowed={reportsAdvanced.allowed}
                onUnlock={() => reportsAdvanced.require()}
              />
            )}
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            {showFilteredEmpty ? (
              <FilteredEmptyState activeCount={currentFilterCount} onClear={clearCurrentFilters} />
            ) : (
              <PaymentsReport
                entries={financeEntries}
                clientNames={clientNames}
                categoryNames={financeCategoryNames}
                sort={sortByTab.payments as PaymentsReportSort}
                loading={paymentsLoading}
                truncated={paymentsTruncated}
                loadFailed={paymentsFailed}
                range={isoRange}
                basis={basis}
                onBasisChange={setBasis}
                allowed={reportsAdvanced.allowed}
                onUnlock={() => reportsAdvanced.require()}
                canSeeFinance={canSeeFinance}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            )}
          </TabsContent>

          <TabsContent value="stock" className="mt-4">
            {showFilteredEmpty ? (
              <FilteredEmptyState activeCount={currentFilterCount} onClear={clearCurrentFilters} />
            ) : (
              <StockReport
                units={trackedProducts}
                productById={productById}
                movements={stockMovements}
                truncated={stockTruncated}
                loading={stockLoading || stockProductsLoading}
                isOnline={isOnline}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                allowed={inventory.allowed}
                onUnlock={() => inventory.require()}
              />
            )}
          </TabsContent>

          <TabsContent value="documents" className="mt-4">
            {showFilteredEmpty ? (
              <FilteredEmptyState activeCount={currentFilterCount} onClear={clearCurrentFilters} />
            ) : (
              <DocumentsReport
                orders={ordersReport}
                contracts={contractsReport}
                receipts={receiptsReport}
                truncated={documentsTruncated}
                loading={documentsLoading}
                loadFailed={documentsFailed}
                isOnline={isOnline}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                allowed={reportsAdvanced.allowed}
                onUnlock={() => reportsAdvanced.require()}
              />
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
